import React, { useState, useEffect } from "react";
import { Form, Input, Button, message, Spin } from "antd";
import styled from "styled-components";
import { API_CONST } from "../../const";

const FormSection = styled.div`
  background: white;
  padding: 24px;
  border-radius: 8px;
  margin-top: 24px;

  .section-title {
    font-size: 18px;
    font-weight: 600;
    color: #014c75;
    margin-bottom: 24px;
  }

  .password-field {
    position: relative;
    
    .change-password-link {
      position: absolute;
      top: 0;
      right: 0;
      color: #00d9a9;
      cursor: pointer;
      font-size: 14px;
      z-index: 1;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }

  .button-group {
    display: flex;
    gap: 12px;
    margin-top: 24px;
    
    .save-button {
      background: #00d9a9;
      border-color: #00d9a9;
      
      &:hover {
        background: #01c49b !important;
        border-color: #01c49b !important;
      }
    }
    
    .back-button {
      background: white;
      border-color: #d9d9d9;
      color: #595959;
      
      &:hover {
        border-color: #00d9a9 !important;
        color: #00d9a9 !important;
      }
    }
  }

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
  }
`;

const MyProfileTab = ({ onBack, onChangePassword, onCompanyConnected }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState(null);

  const isFullAccess = !profileData?.profileRoleId;

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(API_CONST.GET_MY_PROFILE, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 401 || response.status === 403) {
        message.error("Session expired. Please login again.");
        localStorage.removeItem("authToken");
        window.location.href = "/";
        return;
      }

      const data = await response.json();
      if (response.ok) {
        setProfileData(data.profile);
        form.setFieldsValue({
          name: data.profile.name,
          companyName: data.profile.organization,
          mobile: data.profile.contact,
          email: data.profile.email,
          designation: data.profile.designation,
        });
      } else {
        message.error(data.message || "Failed to load profile");
      }
    } catch (error) {
      message.error("Failed to load profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(API_CONST.UPDATE_MY_PROFILE, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: values.name,
          contact: values.mobile,
          organization: values.companyName,
          designation: values.designation,
        }),
      });

      if (response.status === 401 || response.status === 403) {
        message.error("Session expired. Please login again.");
        localStorage.removeItem("authToken");
        window.location.href = "/";
        return;
      }

      const data = await response.json();
      if (response.ok) {
        message.success("Profile updated successfully!");
        localStorage.setItem('userName', values.name);
        setProfileData(data.profile);
        if (values.companyName) {
          const prevOrg = localStorage.getItem("userOrganization");
          if (values.companyName !== prevOrg) {
            onCompanyConnected && onCompanyConnected(values.companyName);
          }
        }
      } else {
        message.error(data.message || "Failed to update profile");
      }
    } catch (error) {
      message.error("Failed to update profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <FormSection>
        <div className="loading-container">
          <Spin size="large" />
        </div>
      </FormSection>
    );
  }

  return (
    <FormSection>
      <h2 className="section-title">Your details</h2>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}>
          <Form.Item
            label="Your Name"
            name="name"
            rules={[{ required: true, message: "Please enter your name" }]}
          >
            <Input placeholder="Enter your name" />
          </Form.Item>

          <Form.Item
            label="Company Name"
            name="companyName"
            rules={[{ required: true, message: "Please enter company name" }]}
            tooltip={!isFullAccess ? "Only full-access users can change the company name" : undefined}
          >
            <Input placeholder="Enter company name" disabled={!isFullAccess} />
          </Form.Item>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}>
          <Form.Item
            label="Mobile Number"
            name="mobile"
            rules={[{ required: true, message: "Please enter mobile number" }]}
          >
            <Input placeholder="Enter mobile number" />
          </Form.Item>

          <Form.Item
            label="Company Email"
            name="email"
          >
            <Input placeholder="Enter company email" disabled />
          </Form.Item>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}>
          <Form.Item
            label="Designation"
            name="designation"
            rules={[{ required: true, message: "Please enter designation" }]}
          >
            <Input placeholder="Enter designation" />
          </Form.Item>

          <div className="password-field">
            <span className="change-password-link" onClick={onChangePassword}>
              Change Password
            </span>
            <Form.Item
              label="Password"
              name="password"
            >
              <Input.Password disabled value="••••••••" placeholder="••••••••" />
            </Form.Item>
          </div>
        </div>

        <div className="button-group">
          <Button type="primary" htmlType="submit" className="save-button" loading={saving}>
            Save Changes
          </Button>
          {onBack && (
            <Button onClick={onBack} className="back-button">
              Back
            </Button>
          )}
        </div>
      </Form>
    </FormSection>
  );
};

export default MyProfileTab;
